import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { collection, getDocs } from "firebase/firestore";
import { app } from "../firebase";
import { db } from "../firebase";
import { resolveCollectionName } from "./attendanceCollections";
import {
  ATTENDANCE_IMAGE_COMPANY_ID,
  attendanceStorageBranchId,
  buildAttendanceImageDayPrefix,
  buildAttendanceImageEmployeePrefix,
  parsePunchTimeFromImageFilename,
  resolveStorageCompanyId,
} from "../config/attendanceImageStorage";

const IMAGE_EXT = /\.(jpe?g|png|webp)$/i;
const FOLDER_BATCH = 12;

function isImageFile(name) {
  return IMAGE_EXT.test(String(name || ""));
}

/**
 * @typedef {{ employeeId: string, employeeName: string, url: string, fileName: string, punchTime: string|null, storagePath: string, capturedAt: Date|null }} AttendanceImageRow
 */

async function urlsForItems(items) {
  const rows = [];
  for (const itemRef of items) {
    if (!isImageFile(itemRef.name)) continue;
    try {
      const url = await getDownloadURL(itemRef);
      const punchTime = parsePunchTimeFromImageFilename(itemRef.name);
      let capturedAt = null;
      if (punchTime) {
        const parts = itemRef.fullPath.split("/");
        const n = parts.length;
        const year = parts[n - 5];
        const month = parts[n - 4];
        const day = parts[n - 3];
        if (year && month && day) {
          const [hh, mm, ss] = punchTime.split(":").map(Number);
          capturedAt = new Date(Number(year), Number(month) - 1, Number(day), hh, mm, ss);
        }
      }
      rows.push({
        url,
        fileName: itemRef.name,
        punchTime,
        storagePath: itemRef.fullPath,
        capturedAt,
      });
    } catch (e) {
      console.warn("getDownloadURL failed:", itemRef.fullPath, e);
    }
  }
  return rows;
}

async function listImagesInFolder(storagePath) {
  const storage = getStorage(app);
  const folderRef = ref(storage, storagePath);
  const listing = await listAll(folderRef);
  return urlsForItems(listing.items);
}

/**
 * List punch images for one employee on one day.
 */
export async function listEmployeeDayImages({ companyId, branchId, ymd, employeeId }) {
  const prefix = buildAttendanceImageEmployeePrefix({ companyId, branchId, ymd, employeeId });
  if (!prefix) return [];
  const files = await listImagesInFolder(prefix);
  return files.map((f) => ({ ...f, employeeId: String(employeeId) }));
}

/**
 * List all employee punch images for a branch + day (lists employee subfolders, batched).
 * @param {{ onProgress?: (n: number) => void }} [opts]
 */
export async function listBranchDayImages({ companyId, branchId, ymd, onProgress }) {
  const dayPrefix = buildAttendanceImageDayPrefix({ companyId, branchId, ymd });
  if (!dayPrefix) return [];

  const storage = getStorage(app);
  const dayRef = ref(storage, dayPrefix);
  let listing;
  try {
    listing = await listAll(dayRef);
  } catch (e) {
    if (e?.code === "storage/object-not-found") return [];
    throw e;
  }

  const prefixes = listing.prefixes || [];
  const directItems = await urlsForItems(listing.items || []);
  const all = [...directItems.map((f) => ({ ...f, employeeId: "" }))];

  for (let i = 0; i < prefixes.length; i += FOLDER_BATCH) {
    const batch = prefixes.slice(i, i + FOLDER_BATCH);
    const parts = await Promise.all(
      batch.map(async (prefixRef) => {
        const employeeId = prefixRef.name;
        try {
          const inner = await listAll(prefixRef);
          const files = await urlsForItems(inner.items);
          return files.map((f) => ({ ...f, employeeId }));
        } catch {
          return [];
        }
      })
    );
    for (const group of parts) all.push(...group);
    onProgress?.(all.length);
  }

  all.sort((a, b) => {
    const ta = a.capturedAt?.getTime?.() ?? 0;
    const tb = b.capturedAt?.getTime?.() ?? 0;
    return ta - tb;
  });
  return all;
}

const EMPLOYEES_COLLECTION = "employees";

function employeeNameFromDoc(docSnap) {
  const d = docSnap.data() || {};
  return String(
    d.name ?? d.Name ?? d.employeeName ?? d.EmployeeName ?? d.fullName ?? docSnap.id
  ).trim();
}

function employeeMatchesShop(d, shopLabel) {
  if (!shopLabel) return true;
  const empBranch = d.branchId ?? d.BranchId ?? d.branch ?? d.BranchID;
  if (empBranch == null || String(empBranch).trim() === "") return true;
  const empStorageBranch = attendanceStorageBranchId(shopLabel, empBranch);
  const shopStorageBranch = attendanceStorageBranchId(shopLabel, null);
  return String(empStorageBranch) === String(shopStorageBranch);
}

/**
 * Load `employees/{employeeId}` docs — doc id matches Storage folder (e.g. `7`).
 * @returns {Promise<{ employees: object[], nameById: Map<string,string>, collection: string }>}
 */
export async function loadEmployeesCollection(shopLabel) {
  const snap = await getDocs(collection(db, EMPLOYEES_COLLECTION));
  const employees = [];
  const nameById = new Map();

  for (const docSnap of snap.docs) {
    const d = docSnap.data() || {};
    const status = String(d.status ?? d.Status ?? "").toLowerCase();
    if (status === "left") continue;
    if (shopLabel && !employeeMatchesShop(d, shopLabel)) continue;

    const employeeId = String(docSnap.id).trim();
    const name = employeeNameFromDoc(docSnap);
    nameById.set(employeeId, name);

    const altId = String(d.employeeId ?? d.EmployeeId ?? "").trim();
    if (altId && altId !== employeeId) nameById.set(altId, name);

    employees.push({
      employeeId,
      name,
      companyId: resolveStorageCompanyId(d.companyId ?? d.CompanyId),
      branchStorageId: attendanceStorageBranchId(shopLabel, d.branchId ?? d.BranchId ?? d.BranchID),
      status: String(d.status ?? d.Status ?? "").trim(),
    });
  }

  employees.sort((a, b) => a.name.localeCompare(b.name));
  return { employees, nameById, collection: EMPLOYEES_COLLECTION };
}

/**
 * Resolve display names for storage folder ids (e.g. `7` → employee name).
 */
export async function loadEmployeeNameMap() {
  try {
    const { nameById } = await loadEmployeesCollection("");
    return nameById;
  } catch {
    return new Map();
  }
}

/**
 * Load employees for attendance branch (working staff).
 * Prefers `employees` collection; falls back to legacy per-branch collections.
 * @param {{ label: string, employeeCandidates?: string[] }} currentShop
 */
export async function loadAttendanceBranchEmployees(currentShop) {
  try {
    const primary = await loadEmployeesCollection(currentShop?.label);
    if (primary.nameById.size > 0 || primary.employees.length > 0) {
      return { employees: primary.employees, collection: primary.collection, nameById: primary.nameById };
    }
  } catch (e) {
    console.warn("employees collection empty or failed:", e);
  }

  const empCandidates = currentShop.employeeCandidates || [];
  const colName = await resolveCollectionName(empCandidates);
  if (!colName) return { employees: [], collection: "", nameById: new Map() };

  const snap = await getDocs(collection(db, colName));
  const employees = [];
  const nameById = new Map();
  for (const docSnap of snap.docs) {
    const d = docSnap.data() || {};
    const status = String(d.Status ?? d.status ?? "").toLowerCase();
    if (status === "left") continue;

    const employeeId = String(
      d.employeeId ?? d.EmployeeId ?? d.empId ?? d.code ?? d.employeeCode ?? docSnap.id
    ).trim();
    const name = employeeNameFromDoc(docSnap);
    nameById.set(employeeId, name);

    const companyId = resolveStorageCompanyId(d.CompanyId ?? d.companyId);
    const branchStorageId = attendanceStorageBranchId(
      currentShop.label,
      d.BranchId ?? d.branchId ?? d.BranchID
    );

    employees.push({
      employeeId,
      name,
      companyId: companyId || ATTENDANCE_IMAGE_COMPANY_ID,
      branchStorageId,
      status: String(d.Status ?? d.status ?? "").trim(),
    });
  }

  employees.sort((a, b) => a.name.localeCompare(b.name));
  return { employees, collection: colName, nameById };
}

export function resolveBranchStorageId(currentShop, employees) {
  const fromEmp = employees.find((e) => e.branchStorageId)?.branchStorageId;
  return attendanceStorageBranchId(currentShop.label, fromEmp);
}
